import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../redux/userSlice";
import { api } from "../../axios/api";
import Swal from "sweetalert2";
import { User, Mail, Phone, Camera, Save, ShieldCheck } from "lucide-react";

const ProfileTab = () => {
  const user = useSelector((state) => state.user.user);
  const language = useSelector((state) => state.language?.currentLanguage || "pt-AO");
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    nome: user?.nome || "",
    telefone: user?.telefone || "",
    email: user?.email || "",
  });

  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoArquivo, setFotoArquivo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingPhoto, setLoadingPhoto] = useState(false);

  const texts = {
    "pt-AO": {
      infoTitle: "Informações da Conta",
      labelName: "Nome Completo",
      labelPhone: "Telefone",
      labelEmail: "E-mail Corporativo",
      btnSave: "Salvar Alterações",
      btnConfirmPhoto: "Confirmar Nova Foto",
      verified: "Conta Verificada",
      placeholderName: "Seu nome",
      placeholderPhone: "+244 ...",
      successMsg: "Dados atualizados com sucesso.",
      photoSuccess: "Foto de perfil atualizada.",
      errorMsg: "Falha ao atualizar dados",
      selectImage: "Selecione uma imagem primeiro."
    },
    "en-US": {
      infoTitle: "Account Information",
      labelName: "Full Name",
      labelPhone: "Phone Number",
      labelEmail: "Corporate Email",
      btnSave: "Save Changes",
      btnConfirmPhoto: "Confirm New Photo",
      verified: "Verified Account",
      placeholderName: "Your name",
      placeholderPhone: "+...",
      successMsg: "Data updated successfully.",
      photoSuccess: "Profile photo updated.",
      errorMsg: "Failed to update data",
      selectImage: "Select an image first."
    },
    "zh-CN": {
      infoTitle: "账户信息",
      labelName: "全名",
      labelPhone: "电话号码",
      labelEmail: "公司邮箱",
      btnSave: "保存更改",
      btnConfirmPhoto: "确认新照片",
      verified: "已验证账户",
      placeholderName: "您的名字",
      placeholderPhone: "电话...",
      successMsg: "数据更新成功。",
      photoSuccess: "个人资料照片已更新。",
      errorMsg: "更新数据失败",
      selectImage: "请先选择一张图片。"
    },
    "fr-FR": {
      infoTitle: "Informations du Compte",
      labelName: "Nom Complet",
      labelPhone: "Téléphone",
      labelEmail: "E-mail Professionnel",
      btnSave: "Enregistrer les Modifications",
      btnConfirmPhoto: "Confirmer la Nouvelle Photo",
      verified: "Compte Vérifié",
      placeholderName: "Votre nom",
      placeholderPhone: "+...",
      successMsg: "Données mises à jour avec succès.",
      photoSuccess: "Photo de profil mise à jour.",
      errorMsg: "Échec de la mise à jour des données",
      selectImage: "Veuillez d'abord sélectionner une image."
    },

    "ar-SA": {
      infoTitle: "معلومات الحساب",
      labelName: "الاسم الكامل",
      labelPhone: "رقم الهاتف",
      labelEmail: "البريد الإلكتروني المؤسسي",
      btnSave: "حفظ التغييرات",
      btnConfirmPhoto: "تأكيد الصورة الجديدة",
      verified: "تم التحقق من الحساب",
      placeholderName: "اسمك",
      placeholderPhone: "+...",
      successMsg: "تم تحديث البيانات بنجاح.",
      photoSuccess: "تم تحديث صورة الملف الشخصي.",
      errorMsg: "فشل في تحديث البيانات",
      selectImage: "يرجى اختيار صورة أولاً."
    }
  };

  const t = texts[language] || texts["pt-AO"];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFotoArquivo(file);
      setFotoPreview(URL.createObjectURL(file));
    }
  };

  const handleUpdateUserData = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.put(`/auth/users/${user.id}/`, formData);
      dispatch(setUser({ user: res.data.data, token: localStorage.getItem("token") }));
      Swal.fire("OK", t.successMsg, "success");
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || t.errorMsg, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePhoto = async () => {
    if (!fotoArquivo) return Swal.fire("!", t.selectImage, "warning");
    setLoadingPhoto(true);
    const data = new FormData();
    data.append("file", fotoArquivo);
    try {
      const res = await api.put(`/auth/users/foto/${user.id}/`, data);
      dispatch(setUser({ user: res.data.data, token: localStorage.getItem("token") }));
      setFotoPreview(null);
      setFotoArquivo(null);
      Swal.fire("OK", t.photoSuccess, "success");
    } catch (err) {
      Swal.fire("Error", "Upload Error", "error");
    } finally {
      setLoadingPhoto(false);
    }
  };

  return (
    <div className="row g-0 animate__animated animate__fadeIn">
      <style>{`
        .win10-input { border: 1px solid #8a8886; border-radius: 0px; font-size: 14px; padding: 8px 12px; }
        .win10-input:focus { border-color: #0078d4; box-shadow: none; outline: 1px solid #0078d4; }
        .win10-btn-primary { background: #0078d4; color: #fff; border: none; border-radius: 0px; padding: 8px 24px; font-size: 14px; }
        .win10-btn-primary:hover { background: #106ebe; }
        .win10-btn-warning { background: #ffaa00; color: #000; border: none; border-radius: 0px; padding: 8px 24px; font-size: 14px; }
        .win10-profile-card { border: 1px solid #e1e1e1; background: #fff; padding: 20px; }
        .win10-label { font-size: 12px; font-weight: 600; color: #323130; margin-bottom: 4px; display: block; }
        .avatar-square { border: 1px solid #e1e1e1; object-fit: cover; background: #f3f2f1; }
      `}</style>

      {/* Lado Esquerdo: Perfil */}
      <div className="col-lg-4 pe-lg-3">
        <div className="win10-profile-card text-center">
          <div className="position-relative d-inline-block mx-auto mb-3">
            <img
              src={fotoPreview || (user?.foto ? `${user.foto}` : "https://ui-avatars.com/api/?name=" + user?.nome)}
              alt="Profile"
              className="avatar-square shadow-sm"
              style={{ width: "160px", height: "160px" }}
            />
            <label htmlFor="upload-photo" className="btn btn-dark btn-sm position-absolute bottom-0 end-0 rounded-0 p-2 border-white border">
              <Camera size={16} />
              <input type="file" id="upload-photo" hidden accept="image/*" onChange={handleFileChange} />
            </label>
          </div>

          <h5 className="fw-bold mb-1 text-uppercase">{user?.nome}</h5>
          <p className="text-muted small mb-4">{user?.role || "System User"}</p>

          {fotoPreview && (
            <button onClick={handleUpdatePhoto} disabled={loadingPhoto} className="win10-btn-warning w-100 mb-2 fw-bold">
              {loadingPhoto ? "..." : t.btnConfirmPhoto}
            </button>
          )}
          
          <div className="bg-light p-2 text-start border">
            <div className="d-flex align-items-center gap-2 text-success small fw-bold">
              <ShieldCheck size={14} /> {t.verified}
            </div>
          </div>
        </div>
      </div>

      {/* Lado Direito: Formulário */}
      <div className="col-lg-8 mt-4 mt-lg-0">
        <div className="win10-profile-card h-100">
          <h6 className="fw-bold mb-4 text-uppercase border-bottom pb-2">{t.infoTitle}</h6>
          <form onSubmit={handleUpdateUserData}>
            <div className="row g-4">
              <div className="col-12">
                <label className="win10-label">{t.labelName}</label>
                <div className="d-flex align-items-center border">
                  <span className="p-2 bg-light border-end"><User size={16} /></span>
                  <input
                    type="text"
                    className="form-control border-0 rounded-0 shadow-none win10-input"
                    name="nome"
                    value={formData.nome}
                    onChange={handleChange}
                    placeholder={t.placeholderName}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <label className="win10-label">{t.labelPhone}</label>
                <div className="d-flex align-items-center border">
                  <span className="p-2 bg-light border-end"><Phone size={16} /></span>
                  <input
                    type="text"
                    className="form-control border-0 rounded-0 shadow-none win10-input"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    placeholder={t.placeholderPhone}
                  />
                </div>
              </div>

              <div className="col-md-6">
                <label className="win10-label">{t.labelEmail}</label>
                <div className="d-flex align-items-center border">
                  <span className="p-2 bg-light border-end"><Mail size={16} /></span>
                  <input
                    type="email"
                    className="form-control border-0 rounded-0 shadow-none win10-input"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="mt-5 text-end border-top pt-3">
              <button type="submit" className="win10-btn-primary fw-bold" disabled={loading}>
                {loading ? "..." : <><Save size={16} className="me-2" /> {t.btnSave}</>}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ProfileTab;